import { useRef, useCallback } from 'react';
import { logSummaryEngagement, logEpisodeEngagement } from '@/hooks/useAnalytics';
import type { AudioType } from '@/contexts/AudioPlayerContext';

// Engagement tracking configuration
const START_THRESHOLD_SECONDS = 30;

export interface TrackIdentifier {
  masterBriefId: string;
  audioType: AudioType;
  source: 'podcast' | 'youtube';
  /** For full_episode plays: the taddy_episode_uuid or youtube_video_id */
  contentId?: string;
}

/**
 * Manages audio engagement milestone tracking (start events).
 * 
 * "start" fires after 30 seconds of accumulated listening time.
 * Completion is tracked via manual is_completed toggles on user_briefs / saved_episodes.
 */
export function useEngagementTracking() {
  const sessionIdRef = useRef<string | null>(null);
  const hasLoggedStartRef = useRef(false);
  const accumulatedListenTimeRef = useRef(0);
  const lastTimeUpdateRef = useRef(0);

  /** Reset all tracking state for a new listening session */
  const resetForNewTrack = useCallback(() => {
    sessionIdRef.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    hasLoggedStartRef.current = false;
    accumulatedListenTimeRef.current = 0;
    lastTimeUpdateRef.current = 0;
  }, []);

  /** 
   * Call on every timeupdate while playing to accumulate listen time.
   * Only counts forward progress (not seeks backward) and reasonable deltas (<2s). 
   */
  const trackListenTime = useCallback((currentPosition: number) => {
    if (lastTimeUpdateRef.current > 0) {
      const delta = currentPosition - lastTimeUpdateRef.current;
      if (delta > 0 && delta < 2) {
        accumulatedListenTimeRef.current += delta;
      }
    }
    lastTimeUpdateRef.current = currentPosition;
  }, []);

  /** Check and fire engagement milestones */
  const checkMilestones = useCallback((
    track: TrackIdentifier | null,
    currentPosition: number,
    audioDuration: number,
  ) => {
    if (!track || !audioDuration || audioDuration <= 0) return;

    const progressPercent = (currentPosition / audioDuration) * 100;

    const baseParams = {
      durationSeconds: Math.round(audioDuration),
      progressSeconds: Math.round(currentPosition),
      progressPercentage: Math.round(progressPercent * 100) / 100,
      sessionId: sessionIdRef.current,
      source: track.source,
    };

    // START milestone: 30 seconds of accumulated listening
    if (!hasLoggedStartRef.current && accumulatedListenTimeRef.current >= START_THRESHOLD_SECONDS) {
      hasLoggedStartRef.current = true;

      if (track.audioType === 'summary') {
        logSummaryEngagement({
          ...baseParams,
          masterBriefId: track.masterBriefId,
          eventType: 'start',
        });
      } else {
        logEpisodeEngagement({
          ...baseParams,
          contentId: track.contentId || track.masterBriefId,
          eventType: 'start',
        });
      }
    }
  }, []);

  return {
    resetForNewTrack,
    trackListenTime,
    checkMilestones,
  };
}
